/**
 * Normalize clinical tab fields (assessment, plan, evidence, etc.) that may be
 * strings or structured objects from the LLM into readable plain text.
 */
export function clinicalTabValueToPlain(v) {
  if (v == null || v === "") return "";
  const t = typeof v;
  if (t === "string") return v;
  if (t === "number" || t === "boolean") return String(v);
  if (Array.isArray(v)) {
    return v
      .map(clinicalTabValueToPlain)
      .filter((x) => x && String(x).trim())
      .join("\n\n");
  }
  if (t === "object") {
    const keys = [
      "text",
      "content",
      "summary",
      "body",
      "narrative",
      "value",
      "description",
      "detail",
      "finding",
      "statement",
    ];
    for (const k of keys) {
      const x = v[k];
      if (x != null && typeof x === "string" && x.trim()) return x;
    }
    const parts = [];
    for (const prop of Object.keys(v)) {
      const val = v[prop];
      if (typeof val === "string" && val.trim()) parts.push(val.trim());
    }
    if (parts.length) return parts.join("\n\n");
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

/**
 * Build Report tab text from structured tabs — Plan and Evidence use the exact
 * same numbered/bulleted content as the A&P tab (not rephrased).
 */
export function composeClinicalReportFromTabs(tabs) {
  if (!tabs || typeof tabs !== "object") return "";

  const lines = [];
  const pushSection = (label, body, emptyFallback = "Not discussed") => {
    const text =
      body != null && String(body).trim() ? String(body).trim() : emptyFallback;
    lines.push(`${label}\n${text}`, "");
  };

  pushSection("Assessment:", clinicalTabValueToPlain(tabs.assessment));
  pushSection("Plan:", clinicalTabValueToPlain(tabs.plan));

  pushSection("Consider:", clinicalTabValueToPlain(tabs.consider));

  const scores = tabs.scores;
  const scoresPlain =
    scores == null || scores === ""
      ? "None mentioned"
      : typeof scores === "string"
        ? scores
        : JSON.stringify(scores, null, 2);
  pushSection("Scores:", scoresPlain, "None mentioned");

  const ev = tabs.evidence;
  const evLines = [];
  if (Array.isArray(ev)) {
    ev.forEach((item) => {
      const t = clinicalTabValueToPlain(item).trim();
      if (!t) return;
      evLines.push(/^(-|\d+\.)\s/.test(t) ? t : `- ${t}`);
    });
  } else if (ev != null && ev !== "") {
    clinicalTabValueToPlain(ev)
      .split("\n")
      .forEach((line) => {
        const t = line.trim();
        if (!t) return;
        evLines.push(/^(-|\d+\.)\s/.test(t) ? t : `- ${t}`);
      });
  }
  lines.push(
    "Evidence:",
    evLines.length ? evLines.join("\n") : "No evidence summary.",
    "",
  );

  return lines.join("\n").trim();
}
